/**
 * Hierarchical section selector for the compare view.
 *
 * Clean number | title rows (matching the Cogni compare-grid selector) with a
 * single "Deselect all" / "Select all" toggle. Check a top-level section (§5) →
 * all its subsections (5.1, 5.2…) turn on; untick any child individually and the
 * parent goes "partial". The selection is the set of section_numbers to SHOW;
 * `undefined` = all (default). Renders in a portal so it isn't clipped by the
 * compare column's overflow-hidden. Per-column: each drug has its own selector.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SectionResponse } from '../types';
import { SECTION_NUMBER_NAMES } from '../utils/drugDetailUtils';
import { useTab } from '../shell/TabContext';
import { effectiveSelection, hasContent, sectionNumbersOf } from '../utils/sectionSelection';

export { effectiveSelection, sectionNumbersOf };

const PANEL_W = 384; // px (w-96)

interface TreeNode {
  key: string; // section_number or synthetic key for unnumbered
  num: string; // shown in the number column ('' for unnumbered)
  title: string;
  children: TreeNode[];
  /** JP-only: canonical PMDA section that's absent from this drug — rendered
   *  greyed + non-selectable so the numbering reads continuously. */
  absent?: boolean;
}

/** Strip a leading "5.1" / "5" (+ separator) the title sometimes repeats. */
function cleanTitle(title: string, num: string): string {
  let t = (title || '').trim();
  if (num) t = t.replace(new RegExp(`^${num.replace(/\./g, '\\.')}\\s*[.:)-]?\\s*`), '').trim();
  return t;
}

// US-SPL pseudo-sections that belong at the TOP of any section ordering: the
// BOXED WARNING (most critical) then HIGHLIGHTS. Lower rank = higher up.
// Everything else (numeric §1..§17, then other letter keys like MISC) sorts
// below these. Non-US labels have neither, so they're unaffected.
// HIGHLIGHTS is usually stored with no section_number, so its key is the
// synthetic `__HIGHLIGHTS OF PRESCRIBING INFORMATION` — match it by title, as
// isHighlightsSection does on the drug-detail page.
const TOP_RANK: Record<string, number> = { BW: 0, HIGHLIGHTS: 1 };
const topRankOf = (k: string): number | undefined => {
  const u = k.replace(/^__/, '').toUpperCase();
  return u.includes('HIGHLIGHTS OF PRESCRIBING INFORMATION') ? TOP_RANK['HIGHLIGHTS'] : TOP_RANK[u];
};

/**
 * Order two `section_number`s for display: BW/HIGHLIGHTS first, then numeric
 * sections ascending (with subsection nesting, e.g. 12 < 12.3 < 13), then any
 * remaining letter keys (MISC, …) alphabetically, then unnumbered sections
 * (`__<title>`) last — matching the drug-detail TOC.
 */
export function compareSectionNumbers(a: string, b: string): number {
  const ra = topRankOf(a);
  const rb = topRankOf(b);
  if (ra !== undefined || rb !== undefined) {
    if (ra !== undefined && rb !== undefined) return ra - rb;
    return ra !== undefined ? -1 : 1; // a BW/HIGHLIGHTS node always leads
  }
  // Unnumbered sections (`__<title>` keys, e.g. Patient Package Insert,
  // Medication Guide) go LAST, alphabetically — as in the drug-detail TOC.
  // Without this, '_' sorts before digits and they float to the top.
  const ua = a.startsWith('__');
  const ub = b.startsWith('__');
  if (ua || ub) return ua && ub ? a.localeCompare(b) : ua ? 1 : -1;
  const pa = a.replace(/^__/, '').split('.').map(Number);
  const pb = b.replace(/^__/, '').split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? -1) - (pb[i] ?? -1);
    if (!Number.isNaN(d) && d !== 0) return d;
  }
  return a.localeCompare(b);
}

function buildTree(sections: SectionResponse[]): TreeNode[] {
  // Keep content-bearing sections AND (JP-only) absent template sections — the
  // latter have no content but must still appear (greyed) so the numbering reads
  // continuously.  Non-JP sections never set `absent`, so this is identical to
  // the old `.filter(hasContent)` for every other region.
  const withContent = sections.filter((s) => hasContent(s) || s.absent);
  const groups = new Map<string, TreeNode>();
  const order: string[] = [];
  // Groups titled by a real top-level row (vs a synthetic parent placeholder).
  const realTitled = new Set<string>();
  for (const s of withContent) {
    const num = s.section_number || '';
    const rawTitle = s.title || '';
    if (!num || !num.includes('.')) {
      // top-level (or unnumbered) node
      const key = num || `__${rawTitle}`;
      const title = cleanTitle(rawTitle, num) || (num ? SECTION_NUMBER_NAMES[num] || '' : rawTitle);
      if (!groups.has(key)) {
        groups.set(key, { key, num, title, children: [], ...(s.absent ? { absent: true } : {}) });
        order.push(key);
      } else {
        // The first real row replaces a synthetic parent's placeholder title; after
        // that keep it (a later repeat, e.g. the package leaflet restarting at 1,
        // must not overwrite it) and only fill it if it was empty.
        const g = groups.get(key)!;
        g.title = realTitled.has(key) ? g.title || title : title || g.title;
      }
      realTitled.add(key);
    } else {
      const topNum = num.split('.')[0]!;
      if (!groups.has(topNum)) {
        // Synthetic parent (no own row) — title from the canonical §N name so it
        // matches the drug-detail TOC ("1 INDICATIONS AND USAGE", not bare "§1").
        groups.set(topNum, { key: topNum, num: topNum, title: SECTION_NUMBER_NAMES[topNum] || '', children: [] });
        order.push(topNum);
      }
      const kids = groups.get(topNum)!.children;
      // Multi-presentation TOCs repeat subsections; selection is by number, so keep the first.
      if (kids.some((c) => c.key === num)) continue;
      kids.push({ key: num, num, title: cleanTitle(rawTitle, num) || num, children: [] });
    }
  }
  const numSort = compareSectionNumbers;
  const nodes = order.map((k) => groups.get(k)!);
  nodes.sort((x, y) => numSort(x.key, y.key));
  for (const n of nodes) n.children.sort((x, y) => numSort(x.key, y.key));
  return nodes;
}

function keysOf(node: TreeNode): string[] {
  return [node.key, ...node.children.map((c) => c.key)];
}

export default function SectionTreeSelect({
  sections,
  selected,
  onChange,
}: {
  sections: SectionResponse[] | undefined;
  selected: string[] | undefined;
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const { isActive } = useTab();
  // The popover is portaled to document.body, so hiding the tab pane does not
  // hide it; close it when the tab goes inactive.
  useEffect(() => {
    if (!isActive) setOpen(false);
  }, [isActive]);
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const tree = useMemo(() => buildTree(sections ?? []), [sections]);
  const allKeys = useMemo(() => sectionNumbersOf(sections), [sections]);
  // Only these keys are real, selectable sections. A synthetic parent (e.g. "1"
  // when only 1.1/1.2 have content) is NOT here, so it must be excluded from a
  // group's tally — otherwise the parent is permanently "indeterminate" even
  // when all its children are on.
  const allSet = useMemo(() => new Set(allKeys), [allKeys]);
  const sel = useMemo(() => effectiveSelection(sections, selected), [sections, selected]);

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const left = Math.min(r.left, window.innerWidth - PANEL_W - 8);
    setPos({ top: r.bottom + 4, left: Math.max(8, left) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const total = allKeys.length;
  const count = allKeys.filter((k) => sel.has(k)).length;
  const allOn = total > 0 && count === total;

  const emit = (next: Set<string>) => onChange(allKeys.filter((k) => next.has(k)));
  const toggleKeys = (keys: string[], on: boolean) => {
    const next = new Set(sel);
    for (const k of keys) (on ? next.add(k) : next.delete(k));
    emit(next);
  };
  // Single toggle: when everything is on → deselect all; otherwise → select all.
  const toggleAll = () => emit(allOn ? new Set() : new Set(allKeys));

  const Row = ({ node, child }: { node: TreeNode; child?: boolean }) => {
    // JP-only: an absent canonical section is non-interactive — no checkbox, not
    // a <label> (so it can't toggle), greyed, with the PMDA "not present"
    // tooltip.  An empty spacer the width of the checkbox keeps columns aligned.
    if (node.absent) {
      return (
        <div
          className="flex cursor-default items-center gap-2.5 px-3 py-1.5"
          title="Not present in the official PMDA package insert for this drug"
        >
          <span className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="w-10 shrink-0 text-xs font-medium text-slate-400">{node.num}</span>
          <span className="truncate text-xs font-semibold text-slate-400" title={node.title}>
            {node.title || '—'}
          </span>
        </div>
      );
    }
    // Only real, selectable keys (exclude a synthetic parent with no own row).
    const groupKeys = (child ? [node.key] : keysOf(node)).filter((k) => allSet.has(k));
    const onCount = groupKeys.filter((k) => sel.has(k)).length;
    const checked = groupKeys.length > 0 && onCount === groupKeys.length;
    const partial = onCount > 0 && !checked;
    return (
      <label
        className={`flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-indigo-50/60 ${child ? 'pl-8' : ''}`}
      >
        <input
          type="checkbox"
          checked={checked}
          ref={(el) => { if (el) el.indeterminate = partial; }}
          onChange={(e) => toggleKeys(groupKeys, e.target.checked)}
          className="h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="w-10 shrink-0 text-xs font-medium text-indigo-600">{node.num}</span>
        <span
          className={`truncate text-xs ${child ? 'text-slate-600' : 'font-semibold text-slate-800'}`}
          title={node.title}
        >
          {node.title || (child ? '' : '—')}
        </span>
      </label>
    );
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
        title="Choose which sections to show"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
        </svg>
        Sections{allOn ? ' · all' : ` · ${count}/${total}`}
        <svg className="h-3 w-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_W }}
          className="z-[60] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl ring-1 ring-black/5"
        >
          <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Sections <span className="font-normal normal-case text-slate-400">· {count}/{total}</span>
            </span>
            <button onClick={toggleAll} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
              {allOn ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <ul className="max-h-[28rem] overflow-y-auto py-1">
            {tree.map((node) => (
              <li key={node.key}>
                <Row node={node} />
                {node.children.map((c) => (
                  <Row key={c.key} node={c} child />
                ))}
              </li>
            ))}
            {tree.length === 0 && <li className="px-3 py-4 text-center text-sm text-slate-400">No sections.</li>}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
